'use client';
import { Logo1Icon } from '@capsicum/common-web/icons';
import { type ComponentProps, type FC, type PropsWithChildren, type ReactNode, useRef } from 'react';
import styled, { type CSSProperties } from 'styled-components';

import { KeyboardAwareBottomWrapper } from '../KeyboardAwareBottomWrapper';

const DEFAULT_GUTTER_BOTTOM = 121;

export type AlignContentTypes = 'center' | 'flex-end' | 'flex-start';
export type ButtonsPositionTypes = 'column' | 'row';

export type ShellProps = ComponentProps<typeof S.Wrapper> &
	PropsWithChildren<{
		alignContent?: AlignContentTypes;
		buttons?: Array<ReactNode>;
		buttonsPosition?: ButtonsPositionTypes;
		scrollOverContainer?: boolean;
		showLogo?: boolean;
		styles?: CSSProperties;
	}>;

export const Shell: FC<ShellProps> = ({
	alignContent = 'center',
	buttons,
	buttonsPosition = 'row',
	children,
	scrollOverContainer = false,
	showLogo = true,
	styles,
	...props
}) => {
	const buttonsContainerRef = useRef<HTMLDivElement>(null);

	return (
		<S.Wrapper style={styles} {...props}>
			{showLogo && <S.Logo />}

			<S.Container>
				<S.ContentContainer
					$alignContent={alignContent}
					$bottomGutter={buttonsContainerRef?.current?.clientHeight}
					$scrollOverContainer={scrollOverContainer}
				>
					{children}
				</S.ContentContainer>
				<KeyboardAwareBottomWrapper>
					{buttons && (
						<S.ButtonContainer $direction={buttonsPosition} ref={buttonsContainerRef}>
							{buttons.map((b, i) => (
								<S.ButtonWrapper key={i}>{b}</S.ButtonWrapper>
							))}
						</S.ButtonContainer>
					)}
				</KeyboardAwareBottomWrapper>
			</S.Container>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		height: 100%;
		width: 100%;
		display: flex;
		flex-grow: 1;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Logo = styled(Logo1Icon)`
		margin: ${({ theme }) => theme.spacings.medium};
		color: ${({ theme }) => theme.colors.icons.main.enabled};
		position: fixed;
		display: block;
		top: 0;
		left: 0;

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			display: none;
		}
	`;

	export const Container = styled.div`
		display: flex;
		position: relative;
		min-height: 0;
		flex-direction: column;
		width: 100%;
		height: auto;

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			flex-grow: 1;
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			max-width: ${({ theme }) => theme.breakpoints.points.mobile.pageWidth}px;
		}
	`;

	export const ButtonContainer = styled.div<{ $direction: ButtonsPositionTypes }>`
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			background: ${({ theme }) => theme.colors.backgrounds.main.translucent};
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			background: ${({ theme }) => theme.colors.backgrounds.main.container};
		}

		display: flex;
		flex-direction: ${({ $direction }) => $direction};
		gap: 20px;
		justify-content: space-around;
		padding: ${({ theme }) => theme.spacings.medium};
		border-top: ${({ theme }) => theme.border.width.medium} solid
			${({ theme }) => theme.colors.borders.main.divider};
	`;

	export const ButtonWrapper = styled.div`
		flex-grow: 1;
	`;

	export const ContentContainer = styled.div<{
		$alignContent: AlignContentTypes;
		$bottomGutter: number | undefined;
		$scrollOverContainer: boolean;
	}>`
		flex-grow: 1;
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.medium};
		${({ $scrollOverContainer }) => $scrollOverContainer && 'overflow: scroll;'}

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			padding: ${({ theme }) => theme.spacings.large};
			padding-bottom: ${({ $bottomGutter }) => ($bottomGutter ? $bottomGutter : DEFAULT_GUTTER_BOTTOM)}px;
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			justify-content: ${({ $alignContent }) => $alignContent};
		}
	`;
}
